// The knowledge-base browser (design §10): search and the proposals counter
// on top, the tree of spaces on the left, the selected page (read view,
// editor or history), a folder listing or the proposals inbox on the right.
// Used by the /knowledge route (location in the URL) and by the project
// page's Knowledge tab (one space, location in local state).

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { KbFolderPolicy, KbProposal, KbTreeEntry } from '@goblic/shared'
import { ApiError, kbConflictOf } from '../../lib/api-client'
import { formatRelative } from '../../lib/format'
import {
  useKbMutations,
  useKbPage,
  useKbPermissions,
  useKbPoliciesFor,
  useKbProposals,
  useKbRevisions,
  useKbRevisionTexts,
  useKbSearch,
  useKbSpaces,
  useKbTrees,
  type KbSpaceInfo,
} from '../../hooks/useKnowledge'
import { useDialogs } from '../dialogs'
import { EmptyState, ErrorNote, LoadingRows, Notice } from '../ui'
import { MomentArt, sceneClass } from '../goblin-art'
import { KbDiff } from './kb-diff'
import { KbEditor } from './kb-editor'
import { KbFolderPolicyDialog } from './kb-folder-policy'
import { KbHistory } from './kb-history'
import { KbPageView } from './kb-page-view'
import { KbProposals } from './kb-proposals'
import { KbSearch } from './kb-search'
import { KbTree } from './kb-tree'
import {
  kbEffectivePolicy,
  kbFolderOf,
  validateKbPath,
  type KbConflict,
  type KbSpace,
  type KbTreeSpace,
} from './kb-types'

export type KbView = 'page' | 'edit' | 'history' | 'new' | 'proposals'

/** Where the browser is. `path` ending in `.md` is a page, anything else a folder. */
export interface KbLocation {
  space?: string
  path?: string
  view?: KbView
}

export interface KnowledgeBrowserProps {
  location: KbLocation
  onNavigate: (location: KbLocation) => void
  /** Restrict to this project's space (project page tab). */
  projectId?: string
  /** Extra element in the toolbar (e.g. "Open in Knowledge"). */
  toolbarExtra?: ReactNode
}

const isPagePath = (path: string | undefined): path is string =>
  !!path && path.endsWith('.md')

/** Stored policies first (they win over a default for the same folder), then defaults. */
function mergePolicies(stored: KbFolderPolicy[], defaults: KbFolderPolicy[]): KbFolderPolicy[] {
  return [...stored, ...defaults.filter((d) => !stored.some((s) => s.folder === d.folder))]
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

export function KnowledgeBrowser({
  location,
  onNavigate,
  projectId,
  toolbarExtra,
}: KnowledgeBrowserProps) {
  const { canWrite, canReview } = useKbPermissions()
  const { spaces, labelOf } = useKbSpaces(projectId)
  const spaceIds = spaces.map((info) => info.space)
  const restrictSpace = projectId ? spaceIds[0] : undefined
  // Seed (starter pages) only the one space the user opened — the project
  // tab, or a space selected in /knowledge — never the all-spaces overview.
  const trees = useKbTrees(spaceIds, { seedSpace: restrictSpace ?? location.space ?? null })
  const policies = useKbPoliciesFor(spaceIds)
  const mutations = useKbMutations()
  const { alert } = useDialogs()
  const proposalsQuery = useKbProposals({ space: restrictSpace })
  const proposalCount = proposalsQuery.data?.proposals.length ?? 0

  const treeSpaces: KbTreeSpace[] = spaces.map((info, index) => {
    const policyData = policies[index]?.data
    return {
      space: info.space as KbSpace,
      label: info.label,
      entries: trees[index]?.data?.entries ?? [],
      policies: policyData ? mergePolicies(policyData.policies, policyData.defaults) : undefined,
    }
  })
  const treesLoading = trees.some((tree) => tree.isLoading)
  const treeError = trees.find((tree) => tree.isError)?.error

  // Search, debounced.
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query), 300)
    return () => window.clearTimeout(timer)
  }, [query])
  const searchQuery = useKbSearch(debounced, restrictSpace)

  // Folder policy dialog.
  const [policyTarget, setPolicyTarget] = useState<{ space: string; folder: string } | null>(null)
  const policyIndex = policyTarget ? spaceIds.indexOf(policyTarget.space) : -1
  const policyData = policyIndex >= 0 ? policies[policyIndex]?.data : undefined
  const ownPolicy =
    policyTarget && policyData
      ? (policyData.policies.find((p) => p.folder === policyTarget.folder)?.agentWrite ?? null)
      : null
  const inheritedPolicy =
    policyTarget && policyData
      ? kbEffectivePolicy(
          mergePolicies(
            policyData.policies.filter((p) => p.folder !== policyTarget.folder),
            policyData.defaults
          ),
          policyTarget.folder
        )
      : null

  function entriesOf(space: string): KbTreeEntry[] {
    return treeSpaces.find((info) => info.space === space)?.entries ?? []
  }

  function createPage(space: string, path: string): void {
    // An existing page opens instead of being overwritten.
    if (entriesOf(space).some((entry) => entry.path === path)) {
      onNavigate({ space, path, view: 'page' })
    } else {
      onNavigate({ space, path, view: 'new' })
    }
  }

  async function movePath(space: string, from: string, to: string): Promise<void> {
    try {
      const result = await mutations.move.mutateAsync({ space, from, to })
      if (location.space === space && location.path) {
        const moved = result.moved.find((m) => m.from === location.path)
        if (moved) {
          onNavigate({ ...location, path: moved.to })
        } else if (!from.endsWith('.md')) {
          // A folder moved: follow it when the user is in it (or below it).
          const fromFolder = `${from.replace(/\/+$/, '')}/`
          const toFolder = `${to.replace(/\/+$/, '')}/`
          const here: string = location.path
          const current = here.endsWith('.md') ? here : `${here.replace(/\/+$/, '')}/`
          if (current.startsWith(fromFolder)) {
            const next = toFolder + current.slice(fromFolder.length)
            onNavigate({
              ...location,
              path: next.endsWith('.md') ? next : next.replace(/\/+$/, ''),
            })
          }
        }
      }
    } catch (error) {
      await alert({
        kind: 'error',
        title: 'Couldn’t move',
        message: errorText(error, 'The move failed.'),
      })
    }
  }

  const view: KbView = location.view ?? 'page'

  let main: ReactNode
  // The landing pane (no space picked) gets the library scene behind it.
  let onOverview = false
  if (view === 'proposals') {
    main = (
      <ProposalsPane
        proposals={proposalsQuery.data?.proposals}
        loading={proposalsQuery.isLoading}
        error={proposalsQuery.error}
        canReview={canReview}
        labelOf={labelOf}
        onOpenPage={(proposal) =>
          onNavigate({ space: proposal.space, path: proposal.path, view: 'page' })
        }
      />
    )
  } else if (location.space && view === 'new' && location.path) {
    main = (
      <NewPagePane
        key={`${location.space}|${location.path}`}
        space={location.space}
        path={location.path}
        spaceLabel={labelOf(location.space)}
        onDone={(path) => onNavigate({ space: location.space, path, view: 'page' })}
        onOpenExisting={() => onNavigate({ space: location.space, path: location.path, view: 'page' })}
        onCancel={() => onNavigate({ space: location.space, path: kbFolderOf(location.path!) || undefined })}
      />
    )
  } else if (location.space && isPagePath(location.path)) {
    main = (
      <PagePane
        key={`${location.space}|${location.path}`}
        space={location.space}
        path={location.path}
        view={view}
        spaceLabel={labelOf(location.space)}
        canWrite={canWrite}
        onNavigate={onNavigate}
      />
    )
  } else if (location.space) {
    main = (
      <FolderPane
        folder={location.path ? `${location.path.replace(/\/+$/, '')}/` : ''}
        spaceLabel={labelOf(location.space)}
        entries={entriesOf(location.space)}
        canWrite={canWrite}
        onOpen={(path) => onNavigate({ space: location.space, path, view: 'page' })}
        onCreate={(path) => createPage(location.space!, path)}
      />
    )
  } else {
    onOverview = true
    main = (
      <OverviewPane
        spaces={spaces}
        treeSpaces={treeSpaces}
        onOpen={(space, path) => onNavigate({ space, path, view: 'page' })}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[14rem] flex-1">
          <KbSearch
            query={query}
            onQueryChange={setQuery}
            results={debounced.trim() && query.trim() ? (searchQuery.data?.hits ?? null) : null}
            loading={searchQuery.isFetching && !searchQuery.data}
            error={searchQuery.isError ? errorText(searchQuery.error, 'Search failed.') : null}
            spaceLabel={labelOf}
            onSelect={(hit) => {
              setQuery('')
              setDebounced('')
              onNavigate({ space: hit.space, path: hit.path, view: 'page' })
            }}
          />
        </div>
        {canReview ? (
          <button
            type="button"
            className={view === 'proposals' ? 'btn-primary' : 'btn-secondary'}
            aria-pressed={view === 'proposals'}
            onClick={() => onNavigate(view === 'proposals' ? {} : { view: 'proposals' })}
          >
            Proposals
            {proposalCount > 0 ? (
              <span
                className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-signal px-1.5 text-xs font-semibold leading-5 text-surface"
                aria-label={`${proposalCount} waiting`}
              >
                {proposalCount}
              </span>
            ) : null}
          </button>
        ) : null}
        {toolbarExtra}
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="panel max-h-[calc(100vh-12rem)] overflow-y-auto p-3 lg:sticky lg:top-0">
          {treeError ? (
            <ErrorNote error={treeError} what="the knowledge base" />
          ) : treesLoading ? (
            <LoadingRows />
          ) : (
            <KbTree
              spaces={treeSpaces}
              selected={
                location.space && isPagePath(location.path)
                  ? { space: location.space as KbSpace, path: location.path }
                  : null
              }
              onSelect={(space, path) => onNavigate({ space, path, view: 'page' })}
              onCreatePage={canWrite ? createPage : undefined}
              onMove={canWrite ? (space, from, to) => void movePath(space, from, to) : undefined}
              onEditFolderPolicy={
                canReview ? (space, folder) => setPolicyTarget({ space, folder }) : undefined
              }
            />
          )}
        </aside>
        <section className={`panel min-w-0 p-5 sm:p-6 ${onOverview ? sceneClass('library') : ''}`}>{main}</section>
      </div>

      <KbFolderPolicyDialog
        open={policyTarget != null}
        onClose={() => {
          setPolicyTarget(null)
          mutations.putPolicies.reset()
        }}
        spaceLabel={policyTarget ? labelOf(policyTarget.space) : ''}
        folder={policyTarget?.folder ?? ''}
        value={ownPolicy}
        inherited={inheritedPolicy}
        saving={mutations.putPolicies.isPending}
        error={
          mutations.putPolicies.isError
            ? errorText(mutations.putPolicies.error, 'Couldn’t save the policy.')
            : null
        }
        onSave={(agentWrite) => {
          if (!policyTarget || !policyData) return
          const others = policyData.policies.filter((p) => p.folder !== policyTarget.folder)
          const next = agentWrite ? [...others, { folder: policyTarget.folder, agentWrite }] : others
          mutations.putPolicies.mutate(
            { space: policyTarget.space, policies: next },
            { onSuccess: () => setPolicyTarget(null) }
          )
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page: read view, editor, history

function PagePane({
  space,
  path,
  view,
  spaceLabel,
  canWrite,
  onNavigate,
}: {
  space: string
  path: string
  view: KbView
  spaceLabel: string
  canWrite: boolean
  onNavigate: (location: KbLocation) => void
}) {
  const { confirm, alert } = useDialogs()
  const mutations = useKbMutations()
  const pageQuery = useKbPage(space, path, { includeDeleted: true })
  const revisionsQuery = useKbRevisions(space, pageQuery.data ? path : null)
  const page = pageQuery.data
  const revisions = revisionsQuery.data?.revisions ?? []
  const applied = revisions.filter((r) => r.status === 'applied')
  const current = page ? applied.find((r) => r.rev === page.rev) : undefined

  const [conflict, setConflict] = useState<KbConflict | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [neededIds, setNeededIds] = useState<string[]>([])
  const raws = useKbRevisionTexts(space, neededIds)
  const [restoringId, setRestoringId] = useState<string | null>(null)

  const go = (next: KbView) => onNavigate({ space, path, view: next })

  if (pageQuery.isLoading) return <LoadingRows label="Loading page…" />
  if (pageQuery.isError) {
    const missing = pageQuery.error instanceof ApiError && pageQuery.error.status === 404
    return missing ? (
      <EmptyState
        title="This page doesn’t exist yet"
        art={null}
        action={
          canWrite ? (
            <button type="button" className="btn-primary" onClick={() => go('new')}>
              Create {path}
            </button>
          ) : undefined
        }
      >
        {spaceLabel} / <span className="font-mono">{path}</span>
      </EmptyState>
    ) : (
      <ErrorNote error={pageQuery.error} what="the page" />
    )
  }
  if (!page) return null

  async function handleDelete(): Promise<void> {
    if (!page) return
    const ok = await confirm({
      kind: 'delete',
      title: `Delete “${page.title}”?`,
      message:
        'People and agents no longer see it. It can be restored from this page for 30 days.',
      confirmLabel: 'Delete page',
    })
    if (!ok) return
    try {
      await mutations.deletePage.mutateAsync({ space, path })
    } catch (error) {
      await alert({ kind: 'error', title: 'Couldn’t delete', message: errorText(error, 'Delete failed.') })
    }
  }

  async function restore(revisionId: string, rev: number | null, deleted: boolean): Promise<void> {
    const ok = await confirm({
      kind: 'question',
      title: deleted ? 'Restore this page?' : `Restore rev ${rev}?`,
      message: deleted
        ? 'The page comes back as it was before it was deleted, as a new revision.'
        : 'Its text becomes the page’s new current revision. Nothing in between is lost.',
      confirmLabel: 'Restore',
    })
    if (!ok) return
    setRestoringId(revisionId)
    try {
      await mutations.restoreRevision.mutateAsync({ space, revisionId })
      if (view === 'history') go('page')
    } catch (error) {
      await alert({ kind: 'error', title: 'Couldn’t restore', message: errorText(error, 'Restore failed.') })
    } finally {
      setRestoringId(null)
    }
  }

  // The newest applied revision that isn't the delete itself.
  const lastLive = applied
    .filter((r) => !r.deleted)
    .sort((x, y) => (y.rev ?? 0) - (x.rev ?? 0))[0]

  if (view === 'edit' && canWrite && !page.deleted) {
    return (
      <KbEditor
        page={page}
        spaceLabel={spaceLabel}
        saving={mutations.savePage.isPending}
        error={saveError}
        conflict={conflict}
        onDismissConflict={() => setConflict(null)}
        onCancel={() => {
          setConflict(null)
          setSaveError(null)
          go('page')
        }}
        onSave={(payload) => {
          setSaveError(null)
          mutations.savePage.mutate(
            { space, ...payload },
            {
              onSuccess: () => {
                setConflict(null)
                go('page')
              },
              onError: (error) => {
                const details = kbConflictOf(error)
                if (details) setConflict(details)
                else setSaveError(errorText(error, 'Couldn’t save the page.'))
              },
            }
          )
        }}
      />
    )
  }

  if (view === 'history') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-ink-3">
              {spaceLabel} / <span className="font-mono text-ink-2">{path}</span>
            </p>
            <h2 className="font-display text-lg font-semibold text-ink">History of {page.title}</h2>
          </div>
          <button type="button" className="btn-ghost btn-sm" onClick={() => go('page')}>
            Back to page
          </button>
        </div>
        {revisionsQuery.isLoading ? (
          <LoadingRows label="Loading history…" />
        ) : revisionsQuery.isError ? (
          <ErrorNote error={revisionsQuery.error} what="the history" />
        ) : (
          <KbHistory
            revisions={revisions}
            currentRev={page.rev}
            raws={raws}
            onNeedContent={(ids) =>
              setNeededIds((current) => [...current, ...ids.filter((id) => !current.includes(id))])
            }
            onRestore={
              canWrite ? (revision) => void restore(revision.id, revision.rev, false) : undefined
            }
            restoringId={restoringId}
          />
        )}
      </div>
    )
  }

  return (
    <KbPageView
      page={page}
      spaceLabel={spaceLabel}
      updatedBy={current?.author}
      onSelectFolder={(folder) => onNavigate({ space, path: folder })}
      onEdit={canWrite && !page.deleted ? () => go('edit') : undefined}
      onShowHistory={() => go('history')}
      onDelete={canWrite && !page.deleted ? () => void handleDelete() : undefined}
      onMarkVerified={
        canWrite && !page.deleted
          ? () =>
              mutations.verify.mutate(
                { space, path },
                {
                  onError: (error) =>
                    void alert({
                      kind: 'error',
                      title: 'Couldn’t mark as accurate',
                      message: errorText(error, 'The request failed.'),
                    }),
                }
              )
          : undefined
      }
      markingVerified={mutations.verify.isPending}
      onRestoreDeleted={
        canWrite && page.deleted && lastLive
          ? () => void restore(lastLive.id, lastLive.rev, true)
          : undefined
      }
      restoringDeleted={restoringId != null}
      resolveRelativeLink={(href) => {
        const target = resolveRelativeKbLink(path, href)
        if (!target) return null
        return {
          href: kbPageHref(space, target),
          navigate: () => onNavigate({ space, path: target, view: 'page' }),
        }
      }}
    />
  )
}

/** App URL of a knowledge page (`/knowledge/<space>/<path>`), for real `href`s. */
export function kbPageHref(space: string, path: string): string {
  return `/knowledge/${encodeURIComponent(space)}/${path.split('/').map(encodeURIComponent).join('/')}`
}

/** `../areas/api.md#x` relative to `decisions/0001.md` → `areas/api.md`; null when it isn't a page path. */
export function resolveRelativeKbLink(fromPath: string, href: string): string | null {
  const clean = href.split('#')[0]!.split('?')[0]!
  if (!clean.endsWith('.md')) return null
  try {
    const resolved = new URL(clean, `https://kb.invalid/${kbFolderOf(fromPath)}`).pathname
    const path = decodeURIComponent(resolved.replace(/^\/+/, ''))
    return validateKbPath(path) ? null : path
  } catch {
    return null
  }
}

function NewPagePane({
  space,
  path,
  spaceLabel,
  onDone,
  onOpenExisting,
  onCancel,
}: {
  space: string
  path: string
  spaceLabel: string
  onDone: (path: string) => void
  /** Opens the page already at `path` (its read view also offers restoring a deleted one). */
  onOpenExisting: () => void
  onCancel: () => void
}) {
  const mutations = useKbMutations()
  const { canWrite } = useKbPermissions()
  const [error, setError] = useState<string | null>(null)
  // 409 on create: a live page already sits at this path.
  const [conflict, setConflict] = useState<KbConflict | null>(null)
  // 409 on create: a deleted page sits at this path (restorable).
  const [deletedRev, setDeletedRev] = useState<number | null>(null)
  if (!canWrite) {
    return <Notice tone="info">You can read the knowledge base, but not add pages.</Notice>
  }

  const editor = (
    <KbEditor
      newPath={path}
      spaceLabel={spaceLabel}
      saving={mutations.savePage.isPending}
      error={error}
      conflict={conflict}
      onDismissConflict={() => setConflict(null)}
      conflictActions={
        <button type="button" className="btn-secondary" onClick={onOpenExisting}>
          Open the existing page
        </button>
      }
      onCancel={onCancel}
      onSave={(payload) => {
        setError(null)
        mutations.savePage.mutate(
          // `baseRev: 0` = "only if nothing is here yet": an existing page
          // answers 409 instead of being silently overwritten.
          { space, ...payload, baseRev: payload.baseRev ?? deletedRev ?? 0 },
          {
            onSuccess: (result) => onDone(result.path),
            onError: (err) => {
              const details = kbConflictOf(err)
              if (!details) {
                setError(errorText(err, 'Couldn’t create the page.'))
              } else if (details.current && !details.current.deleted) {
                setConflict(details)
              } else {
                setDeletedRev(details.currentRev)
              }
            },
          }
        )
      }}
    />
  )

  if (deletedRev != null && !conflict) {
    return (
      <div className="space-y-4">
        <Notice tone="signal">
          <p className="font-medium">A deleted page already exists at this path.</p>
          <p className="mt-0.5 text-sm text-ink-2">
            {spaceLabel} / <span className="font-mono">{path}</span> was deleted (rev {deletedRev}).
            Restore it with its history, or save your new page over it.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn-primary btn-sm" onClick={onOpenExisting}>
              Open it to restore
            </button>
          </div>
        </Notice>
        {editor}
      </div>
    )
  }
  return editor
}

// ---------------------------------------------------------------------------
// Folder listing and overview

function FolderPane({
  folder,
  spaceLabel,
  entries,
  canWrite,
  onOpen,
  onCreate,
}: {
  folder: string
  spaceLabel: string
  entries: KbTreeEntry[]
  canWrite: boolean
  onOpen: (path: string) => void
  onCreate: (path: string) => void
}) {
  const [name, setName] = useState('')
  const inFolder = entries
    .filter((entry) => entry.path.startsWith(folder))
    .sort((x, y) => x.path.localeCompare(y.path))
  const newPath = name.trim()
    ? `${folder}${name.trim().endsWith('.md') ? name.trim() : `${name.trim()}.md`}`
    : ''
  const pathError = newPath ? validateKbPath(newPath) : null

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-ink-3">{spaceLabel}</p>
        <h2 className="font-mono text-lg font-semibold text-ink">{folder || '/'}</h2>
        <p className="mt-0.5 text-sm text-ink-3">
          {inFolder.length === 1 ? '1 page' : `${inFolder.length} pages`}
        </p>
      </div>
      {inFolder.length ? (
        <ul className="divide-y divide-line rounded-panel border border-line">
          {inFolder.map((entry) => (
            <li key={entry.path}>
              <button
                type="button"
                onClick={() => onOpen(entry.path)}
                className="block w-full px-4 py-2.5 text-left hover:bg-sunken/60"
              >
                <span className="flex items-center gap-2 text-base font-medium text-ink">
                  {entry.title}
                  {entry.stale ? <StaleBadge /> : null}
                </span>
                <span className="block font-mono text-xs text-ink-3">{entry.path}</span>
                {entry.summary ? (
                  <span className="mt-0.5 block text-sm text-ink-2">{entry.summary}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-base text-ink-3">No pages here yet.</p>
      )}
      {canWrite ? (
        <form
          className="flex flex-wrap items-start gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (newPath && !pathError) onCreate(newPath)
          }}
        >
          <div className="min-w-[14rem] flex-1">
            <input
              className="field-input font-mono text-sm"
              placeholder="page-name.md"
              aria-label="New page name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {pathError ? <p className="mt-1 text-xs text-danger">{pathError}</p> : null}
          </div>
          <button type="submit" className="btn-secondary" disabled={!newPath || Boolean(pathError)}>
            New page here
          </button>
        </form>
      ) : null}
    </div>
  )
}

function OverviewPane({
  spaces,
  treeSpaces,
  onOpen,
}: {
  spaces: KbSpaceInfo[]
  treeSpaces: KbTreeSpace[]
  onOpen: (space: string, path: string) => void
}) {
  const total = treeSpaces.reduce((sum, info) => sum + info.entries.length, 0)
  const stale = treeSpaces.flatMap((info) =>
    info.entries.filter((entry) => entry.stale).map((entry) => ({ space: info, entry }))
  )
  const recent = useMemo(
    () =>
      treeSpaces
        .flatMap((info) => info.entries.map((entry) => ({ space: info, entry })))
        .sort((x, y) => y.entry.updatedAt.localeCompare(x.entry.updatedAt))
        .slice(0, 8),
    [treeSpaces]
  )

  if (total === 0) {
    return (
      <EmptyState title="No knowledge pages yet">
        Agents write down what they learn about {spaces.length > 1 ? 'your projects' : 'this project'}{' '}
        here, and so can you: hover a space or folder in the tree and press + to add a page.
      </EmptyState>
    )
  }

  const row = (item: { space: KbTreeSpace; entry: KbTreeEntry }) => (
    <li key={`${item.space.space}|${item.entry.path}`}>
      <button
        type="button"
        onClick={() => onOpen(item.space.space, item.entry.path)}
        className="flex w-full flex-wrap items-baseline justify-between gap-x-3 px-4 py-2.5 text-left hover:bg-sunken/60"
      >
        <span className="min-w-0">
          <span className="block text-base font-medium text-ink">{item.entry.title}</span>
          <span className="block text-xs text-ink-3">
            {item.space.label} / <span className="font-mono">{item.entry.path}</span>
          </span>
        </span>
        <span className="text-xs text-ink-3">{formatRelative(item.entry.updatedAt)}</span>
      </button>
    </li>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MomentArt moment="reading" size={72} className="-my-2" />
        <p className="text-base text-ink-2">
          {total === 1 ? '1 page' : `${total} pages`}. Pick one on the left, or search above.
        </p>
      </div>
      {stale.length ? (
        <div>
          <h2 className="mb-2 flex items-center gap-2 font-display text-md font-semibold text-ink">
            May be out of date <StaleBadge count={stale.length} />
          </h2>
          <ul className="divide-y divide-line rounded-panel border border-line">{stale.map(row)}</ul>
        </div>
      ) : null}
      <div>
        <h2 className="mb-2 font-display text-md font-semibold text-ink">Recently changed</h2>
        <ul className="divide-y divide-line rounded-panel border border-line">{recent.map(row)}</ul>
      </div>
    </div>
  )
}

function StaleBadge({ count }: { count?: number }) {
  return (
    <span className="rounded-full bg-signal-soft px-2 text-xs font-medium text-signal">
      {count != null ? count : 'may be out of date'}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Proposals

function ProposalsPane({
  proposals,
  loading,
  error,
  canReview,
  labelOf,
  onOpenPage,
}: {
  proposals: KbProposal[] | undefined
  loading: boolean
  error: unknown
  canReview: boolean
  labelOf: (space: string) => string
  onOpenPage: (proposal: KbProposal) => void
}) {
  const mutations = useKbMutations()
  const { confirm, alert } = useDialogs()
  const [decidingId, setDecidingId] = useState<string | null>(null)

  if (!canReview) {
    return <Notice tone="info">Only approvers and administrators review proposals.</Notice>
  }
  if (loading) return <LoadingRows label="Loading proposals…" />
  if (error) return <ErrorNote error={error} what="the proposals" />

  async function approve(proposal: KbProposal, note: string | undefined): Promise<void> {
    setDecidingId(proposal.id)
    try {
      try {
        await mutations.approveProposal.mutateAsync({ revisionId: proposal.id, note })
      } catch (err) {
        const details = kbConflictOf(err)
        if (!details) throw err
        const force = await confirm({
          kind: 'warning',
          title: 'The page changed since this proposal',
          message: `The proposal is based on rev ${proposal.baseRev}; the page is now at rev ${details.currentRev}. Approving replaces the newer changes.`,
          confirmLabel: 'Approve anyway',
        })
        if (force) {
          await mutations.approveProposal.mutateAsync({ revisionId: proposal.id, note, force: true })
        }
      }
    } catch (err) {
      await alert({ kind: 'error', title: 'Couldn’t approve', message: errorText(err, 'Approve failed.') })
    } finally {
      setDecidingId(null)
    }
  }

  async function reject(proposal: KbProposal, note: string | undefined): Promise<void> {
    setDecidingId(proposal.id)
    try {
      await mutations.rejectProposal.mutateAsync({ revisionId: proposal.id, note })
    } catch (err) {
      await alert({ kind: 'error', title: 'Couldn’t reject', message: errorText(err, 'Reject failed.') })
    } finally {
      setDecidingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">Proposals</h2>
        <p className="mt-0.5 text-sm text-ink-3">
          Agent changes to folders that need review. Approving applies them as a new revision.
        </p>
      </div>
      <KbProposals
        proposals={proposals ?? []}
        spaceLabel={labelOf}
        renderChanges={(proposal) => <ProposalChanges proposal={proposal} />}
        decidingId={decidingId}
        onApprove={(proposal, note) => void approve(proposal, note)}
        onReject={(proposal, note) => void reject(proposal, note)}
        onOpenPage={onOpenPage}
      />
    </div>
  )
}

/** Diff of a proposal against the revision it was based on (`baseRev`). */
function ProposalChanges({ proposal }: { proposal: KbProposal }) {
  const isNew = proposal.isNewPage || !proposal.baseRev
  const revisionsQuery = useKbRevisions(
    isNew ? null : proposal.space,
    isNew ? null : proposal.path
  )
  const base = revisionsQuery.data?.revisions.find(
    (r) => r.status === 'applied' && r.rev === proposal.baseRev
  )
  const raws = useKbRevisionTexts(proposal.space, base ? [base.id] : [])
  if (isNew) {
    return <KbDiff before="" after={proposal.raw} beforeLabel="new page" afterLabel="proposal" />
  }
  if (revisionsQuery.isLoading || (base && raws[base.id] == null)) {
    return <p className="text-sm text-ink-3">Loading changes…</p>
  }
  return (
    <KbDiff
      before={base ? (raws[base.id] ?? '') : ''}
      after={proposal.raw}
      beforeLabel={base ? `rev ${proposal.baseRev}` : 'base not found'}
      afterLabel="proposal"
    />
  )
}
